import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Plus, Ban, Ticket, Eye } from "lucide-react";
import {
  billingApi,
  type PromoCode,
  type PromoCodeType,
  type CreatePromoCodeBody,
} from "./api";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const TYPE_LABEL: Record<PromoCodeType, string> = {
  free: "Gratis",
  percent: "% Descuento",
};

function benefitLabel(p: PromoCode): string {
  if (p.type === "free") return p.durationDays ? `${p.durationDays} días gratis` : "gratis";
  return p.percentOff ? `${p.percentOff}% off` : "descuento";
}

export function PromoCodesScreen() {
  const qc = useQueryClient();
  const { push } = useToast();
  const canManage = usePermission(PERMISSIONS.PROMO_CODES_MANAGE);
  const [onlyActive, setOnlyActive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [viewing, setViewing] = useState<PromoCode | null>(null);

  const q = useQuery({
    queryKey: ["billing", "promo-codes", onlyActive],
    queryFn: () => billingApi.listPromoCodes(onlyActive ? { isActive: "true" } : undefined),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => billingApi.deactivatePromoCode(id),
    onSuccess: () => {
      push({ kind: "success", message: "Código desactivado." });
      qc.invalidateQueries({ queryKey: ["billing", "promo-codes"] });
    },
    onError: () => push({ kind: "error", message: "No se pudo desactivar." }),
  });

  const rows = q.data ?? [];

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Facturación · Códigos promo</p>
          <h1 className="nx-h1">Códigos promocionales</h1>
          <p className="text-sm text-fg-muted mt-1">
            Códigos para regalar módulos Pro a productores elegidos: <strong>gratis</strong> por X días
            (3 meses, 1 año…) o <strong>% de descuento</strong>. El productor los canjea desde su Hub.
            Cada canje queda registrado para seguimiento.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => q.refetch()} className="nx-btn-secondary" disabled={q.isFetching}>
            <RefreshCw size={14} className={q.isFetching ? "animate-spin" : ""} /> Actualizar
          </button>
          {canManage && (
            <button onClick={() => setShowCreate(true)} className="nx-btn-primary">
              <Plus size={14} /> Crear código
            </button>
          )}
        </div>
      </header>

      <label className="flex items-center gap-2 text-xs text-fg-muted">
        <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />
        Mostrar solo activos
      </label>

      {q.isLoading ? (
        <div className="text-sm text-fg-subtle py-10 text-center">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border-subtle rounded-lg py-12 text-center text-sm text-fg-muted">
          No hay códigos {onlyActive ? "activos" : ""} todavía.
        </div>
      ) : (
        <div className="overflow-x-auto border border-border-subtle rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle bg-bg-subtle text-2xs uppercase tracking-wide text-fg-subtle">
                <th className="text-left font-semibold px-3 py-2">Código</th>
                <th className="text-left font-semibold px-3 py-2">Tipo</th>
                <th className="text-left font-semibold px-3 py-2">Módulo</th>
                <th className="text-left font-semibold px-3 py-2">Beneficio</th>
                <th className="text-left font-semibold px-3 py-2">Usos</th>
                <th className="text-left font-semibold px-3 py-2">Vence</th>
                <th className="text-left font-semibold px-3 py-2">Estado</th>
                <th className="text-right font-semibold px-3 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-border-subtle last:border-0 hover:bg-bg-hover">
                  <td className="px-3 py-2 font-mono font-semibold text-fg">{p.code}</td>
                  <td className="px-3 py-2 text-xs text-fg-muted">{TYPE_LABEL[p.type]}</td>
                  <td className="px-3 py-2 text-xs text-fg-muted">{p.skuKey ?? "cualquiera"}</td>
                  <td className="px-3 py-2 text-xs text-fg">{benefitLabel(p)}</td>
                  <td className="px-3 py-2 text-xs text-fg-subtle">
                    {p.currentUses}{p.maxRedemptions != null ? ` / ${p.maxRedemptions}` : ""}
                  </td>
                  <td className="px-3 py-2 text-xs text-fg-subtle">
                    {p.validUntil ? new Date(p.validUntil).toLocaleDateString("es-CL") : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {p.isActive ? (
                      <span className="text-success">Activo</span>
                    ) : (
                      <span className="text-fg-subtle">Inactivo</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button className="nx-btn-secondary mr-1" onClick={() => setViewing(p)}>
                      <Eye size={13} /> Canjes
                    </button>
                    {canManage && p.isActive && (
                      <button
                        className="nx-btn-danger"
                        disabled={deactivate.isPending}
                        onClick={async () => {
                          const ok = await confirmAction({
                            title: "Desactivar código",
                            message: `${p.code} dejará de poder canjearse. Los canjes ya hechos siguen vigentes.`,
                            confirmLabel: "Desactivar",
                            tone: "danger",
                          });
                          if (ok === true) deactivate.mutate(p.id);
                        }}
                      >
                        <Ban size={13} /> Desactivar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && canManage && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ["billing", "promo-codes"] });
          }}
        />
      )}

      {viewing && <RedemptionsModal promo={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { push } = useToast();
  const [code, setCode] = useState("");
  const [type, setType] = useState<PromoCodeType>("free");
  const [skuKey, setSkuKey] = useState("");
  const [durationDays, setDurationDays] = useState("90");
  const [percentOff, setPercentOff] = useState("50");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [perSubjectLimit, setPerSubjectLimit] = useState("1");
  const [validUntil, setValidUntil] = useState("");
  const [note, setNote] = useState("");

  const skusQ = useQuery({ queryKey: ["billing", "skus"], queryFn: () => billingApi.listSkus() });
  const skus = skusQ.data ?? [];

  const create = useMutation({
    mutationFn: () => {
      const body: CreatePromoCodeBody = {
        code,
        type,
        skuKey: skuKey || null,
        maxRedemptions: maxRedemptions ? Number(maxRedemptions) : null,
        perSubjectLimit: Number(perSubjectLimit) || 1,
        validUntil: validUntil || null,
        note: note || null,
      };
      if (type === "free") body.durationDays = Number(durationDays);
      else body.percentOff = Number(percentOff);
      return billingApi.createPromoCode(body);
    },
    onSuccess: () => {
      push({ kind: "success", message: "Código creado." });
      onDone();
    },
    onError: (e: any) =>
      push({ kind: "error", message: e?.response?.data?.error || "No se pudo crear el código." }),
  });

  // FREE exige un módulo objetivo; PERCENT puede aplicar a cualquiera.
  const canSave =
    code.trim().length > 0 &&
    (type === "percent" ? Number(percentOff) >= 1 && Number(percentOff) <= 99 : !!skuKey && Number(durationDays) > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-10">
      <div className="w-full max-w-lg rounded-lg bg-bg-base border border-border-subtle shadow-xl p-5 flex flex-col gap-3">
        <h2 className="text-base font-semibold text-fg">Crear código promocional</h2>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-fg-muted">Código</span>
            <input
              className="nx-input font-mono uppercase"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s+/g, ""))}
              placeholder="VERANO3MESES"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-fg-muted">Tipo</span>
            <select className="nx-input" value={type} onChange={(e) => setType(e.target.value as PromoCodeType)}>
              <option value="free">Gratis por X días</option>
              <option value="percent">% de descuento</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 col-span-2">
            <span className="text-xs text-fg-muted">
              Módulo {type === "free" ? "(obligatorio)" : "(opcional — vacío = cualquiera)"}
            </span>
            <select className="nx-input" value={skuKey} onChange={(e) => setSkuKey(e.target.value)}>
              <option value="">{type === "free" ? "— Elegí un módulo —" : "Cualquier módulo"}</option>
              {skus.map((s) => (
                <option key={s.skuKey} value={s.skuKey}>
                  {s.name} ({s.skuKey})
                </option>
              ))}
            </select>
          </label>

          {type === "free" ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">Días de acceso gratis</span>
              <input className="nx-input" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} placeholder="90" />
            </label>
          ) : (
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">% de descuento (1–99)</span>
              <input className="nx-input" value={percentOff} onChange={(e) => setPercentOff(e.target.value)} placeholder="50" />
            </label>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-fg-muted">Límite por club</span>
            <input className="nx-input" value={perSubjectLimit} onChange={(e) => setPerSubjectLimit(e.target.value)} placeholder="1" />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs text-fg-muted">Tope total (vacío = ilimitado)</span>
            <input className="nx-input" value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} placeholder="∞" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-fg-muted">Vence (vacío = nunca)</span>
            <input type="date" className="nx-input" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </label>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-fg-muted">Nota interna</span>
          <input className="nx-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Campaña verano / acuerdo con productor X" />
        </label>

        {type === "percent" && (
          <p className="text-2xs text-fg-subtle">
            El % de descuento se aplica al comprar el módulo (en despliegue). Por ahora podés crear el
            código; el canje con descuento se habilita en el próximo release.
          </p>
        )}

        <div className="flex items-center justify-end gap-2 mt-1">
          <button onClick={onClose} className="nx-btn-secondary">Cancelar</button>
          <button onClick={() => create.mutate()} disabled={!canSave || create.isPending} className="nx-btn-primary">
            {create.isPending ? "Creando…" : "Crear código"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RedemptionsModal({ promo, onClose }: { promo: PromoCode; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["billing", "promo-codes", promo.id, "redemptions"],
    queryFn: () => billingApi.listRedemptions(promo.id),
  });
  const rows = q.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-10">
      <div className="w-full max-w-xl rounded-lg bg-bg-base border border-border-subtle shadow-xl p-5 flex flex-col gap-3">
        <h2 className="text-base font-semibold text-fg flex items-center gap-2">
          <Ticket size={16} /> Canjes de <span className="font-mono">{promo.code}</span>
        </h2>
        {q.isLoading ? (
          <div className="text-sm text-fg-subtle py-8 text-center">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-fg-muted py-8 text-center">Este código no se ha canjeado todavía.</div>
        ) : (
          <div className="overflow-x-auto border border-border-subtle rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle bg-bg-subtle text-2xs uppercase tracking-wide text-fg-subtle">
                  <th className="text-left font-semibold px-3 py-2">Sujeto</th>
                  <th className="text-left font-semibold px-3 py-2">Módulo</th>
                  <th className="text-left font-semibold px-3 py-2">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border-subtle last:border-0">
                    <td className="px-3 py-2 text-xs">{r.subjectType} #{r.subjectId}</td>
                    <td className="px-3 py-2 text-xs text-fg-muted">{r.skuKey ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-fg-subtle">{new Date(r.redeemedAt).toLocaleString("es-CL")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={onClose} className="nx-btn-secondary">Cerrar</button>
        </div>
      </div>
    </div>
  );
}
